import React, { useMemo, useState } from 'react';
import { ArrowRight, Building2, Check, Globe, Mail, User } from 'lucide-react';
import { leadsApi } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

type PropertiesPerMonthId = '1-5' | '6-20' | '21-50' | '50+';

const options: { id: PropertiesPerMonthId; label: string }[] = [
  { id: '21-50', label: 'Large scale (21–50)' },
  { id: '50+', label: 'Enterprise (50+)' },
];

const CustomQuoteSection: React.FC = () => {
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [propertiesPerMonth, setPropertiesPerMonth] = useState<PropertiesPerMonthId | ''>('');
  const [projectDetails, setProjectDetails] = useState('');

  const canSubmit = useMemo(() => {
    if (!name.trim()) return false;
    if (!companyName.trim()) return false;
    if (!email.trim() || !email.includes('@')) return false;
    if (!website.trim()) return false;
    if (!propertiesPerMonth) return false;
    return true;
  }, [name, companyName, email, website, propertiesPerMonth]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!canSubmit) {
      showToast('Please fill in all required fields.', 'warning');
      return;
    }
    setIsSubmitting(true);
    try {
      await leadsApi.submitCustomQuote({
        name: name.trim(),
        company_name: companyName.trim(),
        email: email.trim().toLowerCase(),
        website: website.trim(),
        properties_per_month: propertiesPerMonth,
        project_details: projectDetails.trim() ? projectDetails.trim() : undefined,
      });
      showToast('Thanks! We received your request and will reach out shortly.', 'success');
      setName('');
      setCompanyName('');
      setEmail('');
      setWebsite('');
      setPropertiesPerMonth('');
      setProjectDetails('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not submit. Please try again.';
      showToast(msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="pricing" className="py-14 sm:py-18 md:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="font-manrope text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
              Get a Custom Quote
            </h2>
            <p className="mt-3 text-gray-600 text-base sm:text-lg">
              Tell us a bit about your needs and we&apos;ll reply with the best option for your volume.
            </p>
          </div>

          <div className="card-hover bg-white border border-[#E2E8F0] rounded-3xl p-6 sm:p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none"
                      placeholder="Your name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Company name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none"
                      placeholder="Company name"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your email (professional) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none"
                      placeholder="you@company.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your website <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none"
                      placeholder="yourwebsite.com"
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  How many properties videos do you need per month? <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {options.map((opt) => (
                    <button
                      type="button"
                      key={opt.id}
                      onClick={() => setPropertiesPerMonth(opt.id)}
                      className={`py-3 px-4 rounded-xl border-2 font-medium transition-all text-left ${
                        propertiesPerMonth === opt.id
                          ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      {propertiesPerMonth === opt.id && <Check className="inline-block w-4 h-4 mr-2" />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tell us about your project <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={projectDetails}
                  onChange={(e) => setProjectDetails(e.target.value)}
                  rows={5}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none"
                  placeholder="Any details about your properties, timeline, or specific needs"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className={`btn-premium w-full h-[52px] rounded-full font-bold text-white transition-all flex items-center justify-center gap-2 ${
                    !canSubmit || isSubmitting
                      ? 'opacity-50 cursor-not-allowed bg-[#3C59B2]'
                      : 'bg-[#3C59B2] hover:bg-[#2d4a8f]'
                  }`}
                >
                  {isSubmitting ? 'Submitting…' : 'Request my quote'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomQuoteSection;

